import React, { useState, useEffect, useCallback } from 'react';

const SmsModal = ({ isOpen, onClose, onSend, isSending, contactCount, result }) => {
  const [message, setMessage] = useState('');
  const [characterCount, setCharacterCount] = useState(0);
  const [smsPreview, setSmsPreview] = useState(false);
  const maxLength = 160;
  
  // Reset form when modal opens - memoized for better performance
  useEffect(() => {
    if (isOpen) {
      setMessage('');
      setSmsPreview(false);
    }
  }, [isOpen]);
  
  // Update character count when message changes - debounced for performance
  useEffect(() => {
    setCharacterCount(message.length);
  }, [message]);
  
  // Calculate SMS metrics - memoized to reduce recalculations
  const getSmsMetrics = useCallback(() => {
    const singleSmsLength = 160;
    const smsCount = Math.ceil(characterCount / singleSmsLength);
    const progress = (characterCount / singleSmsLength) * 100;
    const progressColor = 
      characterCount > 150 ? 'bg-red-500' : 
      characterCount > 100 ? 'bg-yellow-500' : 
      'bg-green-500';
    
    return {
      smsCount,
      progress: Math.min(progress, 100),
      progressColor
    };
  }, [characterCount]);
  
  // Format phone for display
  const formatPhone = () => '+216 XX XXX XXX';
  
  if (!isOpen) return null;
  
  const { smsCount, progress, progressColor } = getSmsMetrics();
  
  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
      <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay with blur - simplified for performance */}
        <div 
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" 
          aria-hidden="true"
          onClick={onClose}
        ></div>
        
        {/* Modal panel */}
        <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
        <div className="inline-block align-bottom bg-white rounded-xl px-4 pt-5 pb-4 text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full sm:p-6">
          {/* Close button */}
          <div className="absolute top-0 right-0 pt-4 pr-4">
            <button
              type="button"
              className="bg-white rounded-md text-gray-400 hover:text-gray-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540]"
              onClick={onClose}
            >
              <span className="sr-only">Close</span>
              <svg className="h-6 w-6" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
          
          <div className="sm:flex sm:items-start">
            <div className="mx-auto flex-shrink-0 flex items-center justify-center h-12 w-12 rounded-full bg-[#174540] bg-opacity-10 sm:mx-0 sm:h-10 sm:w-10">
              <svg className="h-6 w-6 text-[#174540]" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
              </svg>
            </div>
            <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left">
              <h3 className="text-lg leading-6 font-semibold text-gray-900" id="modal-title">
                Envoyer un SMS
              </h3>
              <div className="mt-1">
                <div className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-[#174540] bg-opacity-10 text-[#174540]">
                  {contactCount} destinataire{contactCount > 1 ? 's' : ''}
                </div>
              </div>
            </div>
          </div>
          
          <div className="mt-6 space-y-5">
            {/* Message textarea with enhanced styling */}
            <div className="relative">
              <div className="flex items-center justify-between mb-2">
                <label htmlFor="message" className="block text-sm font-medium text-gray-700 flex items-center">
                  <svg className="mr-1.5 h-4 w-4 text-[#174540]" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10c0 3.866-3.582 7-8 7a8.841 8.841 0 01-4.083-.98L2 17l1.338-3.123C2.493 12.767 2 11.434 2 10c0-3.866 3.582-7 8-7s8 3.134 8 7zM7 9H5v2h2V9zm8 0h-2v2h2V9zM9 9h2v2H9V9z" clipRule="evenodd" />
                  </svg>
                  Votre message
                </label>
                <button 
                  type="button" 
                  className="text-sm text-[#174540] hover:text-[#0d2926] font-medium flex items-center px-2 py-1 rounded-md hover:bg-gray-100"
                  onClick={() => setSmsPreview(!smsPreview)}
                >
                  {smsPreview ? 'Modifier' : 'Aperçu'}
                  <svg className="ml-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path d={smsPreview 
                      ? "M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" 
                      : "M10 12a2 2 0 100-4 2 2 0 000 4z M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"} />
                  </svg>
                </button>
              </div>
              
              {smsPreview ? (
                <div className="bg-gray-100 rounded-lg p-4 shadow-sm border border-gray-200">
                  <div className="bg-white rounded-lg p-3 shadow-sm border border-gray-200 max-w-[85%] ml-auto my-2">
                    <div className="text-sm">{message || "Votre message apparaîtra ici..."}</div>
                    <div className="text-right mt-1 text-xs text-gray-400">
                      {new Date().toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
                    </div>
                  </div>
                  <div className="text-xs text-center mt-2 text-gray-500">
                    Envoyé à {formatPhone()}
                    {contactCount > 1 && ` et ${contactCount - 1} autre${contactCount > 2 ? 's' : ''}`}
                  </div>
                </div>
              ) : (
                <div className="relative">
                  <textarea
                    id="message"
                    name="message"
                    rows={4}
                    className="shadow-sm focus:ring-2 focus:ring-[#174540] focus:border-[#174540] block w-full sm:text-sm border-gray-300 rounded-lg p-4 resize-none"
                    placeholder="Écrivez votre message ici..."
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    maxLength={maxLength}
                    disabled={isSending}
                  ></textarea>
                  
                  {/* Enhanced character indicator - appears when typing */}
                  {characterCount > 0 && (
                    <div className="absolute bottom-3 right-3">
                      <div className={`
                        rounded-full h-7 w-7 flex items-center justify-center text-xs font-medium
                        ${characterCount > maxLength - 20 ? 'bg-red-100 text-red-800' : 'bg-[#174540]/10 text-[#174540]'}
                      `}>
                        {maxLength - characterCount}
                      </div>
                    </div>
                  )}
                </div>
              )}
              
              {/* Enhanced character counter with progress bar */}
              <div className="mt-2">
                <div className="flex justify-between items-center text-xs mb-1">
                  <span className={characterCount > maxLength - 20 ? 'text-red-500 font-medium' : 'text-gray-500'}>
                    {characterCount}/{maxLength} caractères
                  </span>
                  <span className="text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
                    {smsCount} SMS
                  </span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-1.5">
                  <div 
                    className={`${progressColor} h-1.5 rounded-full`} 
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
              </div>
            </div>
            
            {/* Estimated cost information */}
            <div className="rounded-md bg-blue-50 p-3 border border-blue-100">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-blue-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <p className="text-sm text-blue-700">
                    Coût estimé: <span className="font-semibold">{(0.04 * contactCount * smsCount).toFixed(3)} TND</span>
                  </p>
                </div>
              </div>
            </div>
          </div>
          
          {/* Add result notification inside the modal */}
          {result && (
            <div className={`mt-4 rounded-md ${result.success ? 'bg-green-50' : 'bg-red-50'} p-3`}>
              <div className="flex">
                <div className="flex-shrink-0">
                  {result.success ? (
                    <svg className="h-5 w-5 text-green-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  ) : (
                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  )}
                </div>
                <div className="ml-3 flex-1">
                  <p className={`text-sm ${result.success ? 'text-green-700' : 'text-red-700'}`}>
                    {result.message}
                  </p>
                </div>
              </div>
            </div>
          )}
          
          <div className="mt-6 sm:flex sm:flex-row-reverse">
            <button
              type="button"
              className={`w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-[#174540] text-base font-medium text-white hover:bg-[#0d2926] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:ml-3 sm:w-auto sm:text-sm ${
                !message.trim() || isSending ? 'opacity-50 cursor-not-allowed' : ''
              }`}
              onClick={() => message.trim() && !isSending && onSend(message)}
              disabled={!message.trim() || isSending}
            >
              {isSending ? (
                <>
                  <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Envoi en cours...
                </>
              ) : (
                <>
                  <svg className="-ml-1 mr-1.5 h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                  </svg>
                  Envoyer
                </>
              )}
            </button>
            <button
              type="button"
              className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#174540] sm:mt-0 sm:w-auto sm:text-sm"
              onClick={onClose}
              disabled={isSending}
            >
              Annuler
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SmsModal;